import { useRef, useEffect, type MutableRefObject } from 'react';

/**
 * Hook that returns a ref with the latest value.
 * Useful for keeping callbacks stable while accessing latest props/state.
 *
 * This pattern allows you to create callbacks with useCallback that have no dependencies,
 * while still accessing the latest values of props and state. This prevents unnecessary
 * re-renders of child components that receive these callbacks as props.
 *
 * @param value - The value to keep in sync with the ref
 * @returns A ref object containing the latest value
 *
 * @example
 * ```tsx
 * function Component({ onSubmit }: { onSubmit: (data: FormData) => void }) {
 *   const onSubmitRef = useLatest(onSubmit);
 *
 *   // This callback never changes, preventing child re-renders
 *   const handleSubmit = useCallback((data: FormData) => {
 *     // Always calls the latest onSubmit, even if prop changes
 *     onSubmitRef.current(data);
 *   }, []); // No dependencies needed!
 *
 *   return <Form onSubmit={handleSubmit} />;
 * }
 * ```
 *
 * @example
 * ```tsx
 * function SearchInput({ query, onSearch }: Props) {
 *   const queryRef = useLatest(query);
 *   const onSearchRef = useLatest(onSearch);
 *
 *   // Stable callback that always uses latest values
 *   const handleKeyPress = useCallback((e: KeyboardEvent) => {
 *     if (e.key === 'Enter') {
 *       onSearchRef.current(queryRef.current);
 *     }
 *   }, []);
 *
 *   useEffect(() => {
 *     window.addEventListener('keydown', handleKeyPress);
 *     return () => window.removeEventListener('keydown', handleKeyPress);
 *   }, [handleKeyPress]); // handleKeyPress never changes
 *
 *   return <input value={query} onChange={...} />;
 * }
 * ```
 */
export function useLatest<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);

  // Update ref after every render (not in useEffect to ensure sync)
  // Using useEffect instead of direct assignment to follow React best practices
  useEffect(() => {
    ref.current = value;
  });

  return ref;
}
